/* eslint-disable eslint-plugin-next/no-html-link-for-pages */

import { riseAlotTween, riseTween } from '@/components/Giveback2020/Giveback2020Animations';
import Giveback2020Floater from '@/components/Giveback2020/Giveback2020Floater';
import Giveback2020Covid from '@/components/Giveback2020/shapes/Giveback2020Covid';
import Giveback2020Families from '@/components/Giveback2020/shapes/Giveback2020Families';
import Giveback2020FinalDot from '@/components/Giveback2020/shapes/Giveback2020FinalDot';
import Giveback2020Finito from '@/components/Giveback2020/shapes/Giveback2020Finito';
import Giveback2020LGBT from '@/components/Giveback2020/shapes/Giveback2020LGBT';
import Giveback2020Speaker from '@/components/Giveback2020/shapes/Giveback2020Speaker';
import Giveback2020Students from '@/components/Giveback2020/shapes/Giveback2020Students';
import Giveback2020Trees from '@/components/Giveback2020/shapes/Giveback2020Trees';
import Giveback2020Voters from '@/components/Giveback2020/shapes/Giveback2020Voters';
import Giveback2020Zoo from '@/components/Giveback2020/shapes/Giveback2020Zoo';
import GivebackInitialTitle from '@/components/GivebackShared/GivebackInitialTitle';
import GivebackShareButton, { share } from '@/components/GivebackShared/GivebackShareButton';
import Tweener from '@/components/GivebackShared/Tweener';
import Layout from '@/components/shared/Layout';
import { SEO } from '@/components/shared/SEO';
import useOptionalTween from '@/hooks/useOptionalTween';
import useTrackPad from '@/hooks/useTrackPad';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/giveback_2020/og.png';
import { Box, Flex, font, themedColor, useIsomorphicLayoutEffect, usePageScroll } from '@lemonade-hq/cdk';
import { Button, MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { useNavContext } from '@lemonade-hq/nuiza';
import dynamic from 'next/dynamic';
import React, { useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { GIVEBACK_LANDING_PAGE } from '../utils/productAdaPages';

const Giveback2020RisingNumbers = dynamic(() => import('@/components/Giveback2020/Giveback2020RisingNumbers'), {
  ssr: false,
});

const Giveback2020Path = dynamic(() => import('@/components/Giveback2020/Giveback2020Path'), { ssr: false });
const Giveback2020RisingParagraph = dynamic(() => import('@/components/Giveback2020/Giveback2020RisingParagraph'), {
  ssr: false,
});

const GlobalStyle = createGlobalStyle`
    footer p {
        display: none;
    }
`;

const Container = styled(Flex)`
  ${MEDIA_QUERIES.upToDesktop`
        margin-top: 30px;
    `}

  ${MEDIA_QUERIES.desktop`
        margin-top: 50px;
    `}

    width: 100%;
  overflow: hidden;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  align-items: center;
  flex-direction: column;
  position: relative;
`;

const MobileText = styled.span`
  display: inline;

  ${MEDIA_QUERIES.desktop`
        display: none;
    `}
`;

const DesktopText = styled.span`
  display: none;

  ${MEDIA_QUERIES.desktop`
        display: inline;
    `}
`;

const PageContainer = styled(Flex)`
  align-items: center;
  flex-direction: column;
  position: relative;

  ${MEDIA_QUERIES.upToDesktop`
        width: 375px;
    `}

  ${MEDIA_QUERIES.desktop`
        width: 1024px;
    `}
`;

const PathContainer = styled(Flex)`
  top: 0;
  position: absolute;
  overflow: hidden;
  pointer-events: none;
  align-items: center;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  width: 100%;
  will-change: contents;
  z-index: 2;
`;

const Odometer = styled(Giveback2020RisingNumbers)`
  ${MEDIA_QUERIES.upToDesktop`
        margin-top: 5px;
        letter-spacing: -2px;
        ${font('main', {
          fontSize: '46px',
          lineHeight: '46px',
          fontWeight: '900',
        })};
    `}

  ${MEDIA_QUERIES.desktop`
        letter-spacing: -4px;
        transform: translateY(-5px);
        ${font('main', {
          fontSize: '130px',
          lineHeight: '130px',
          fontWeight: '900',
        })};
    `};
`;

const Title = styled(Box)`
  width: 100%;
  user-select: none;

  ${MEDIA_QUERIES.upToDesktop`
        letter-spacing: -2px;
        ${font('main', {
          fontSize: '46px',
          lineHeight: '46px',
          fontWeight: '900',
        })};
    `}

  ${MEDIA_QUERIES.desktop`
        letter-spacing: -4px;
        ${font('main', {
          fontSize: '130px',
          lineHeight: '130px',
          fontWeight: '900',
        })};
    `};
`;

const Subtitle = styled(Box)`
  width: 100%;

  ${MEDIA_QUERIES.upToDesktop`
        ${font('main', { fontSize: '16px', lineHeight: '20.96px' })};
    `}

  ${MEDIA_QUERIES.desktop`
        ${font('main', { fontSize: '24px', lineHeight: '30px' })};
    `};
`;

const SmallerSubtitle = styled(Box)`
  width: 100%;

  ${MEDIA_QUERIES.upToDesktop`
        ${font('main', { fontSize: '15px', lineHeight: '24px' })};
    `}

  ${MEDIA_QUERIES.desktop`
        ${font('main', { fontSize: '18px', lineHeight: '23.94px' })};
    `};
`;

const SmallerSubtitleLink = styled(SmallerSubtitle)`
  text-decoration: underline;
  color: ${themedColor('primary')};
  cursor: pointer;

  &:hover {
    color: ${themedColor('darkPrimary')};
  }
`;

const GrayBackground = styled(Box)`
  position: absolute;
  background-color: ${themedColor('cloud')};
  z-index: 0;
  width: 100vw;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    left: -25%;
    width: 200%;
  }
`;

const Gradient = styled(Box)`
  width: 100vw;
  position: absolute;
  bottom: 0;
  z-index: 1;
  background: linear-gradient(to bottom, black 0%, rgb(40, 40, 40) 100%);

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    left: -40%;
    width: 200%;
  }
`;

export const Giveback2020Internal = React.memo(({ t }: { t: number }) => {
  const { nav } = useNavContext();

  return (
    <Container>
      <GlobalStyle />
      <PageContainer>
        <Odometer mb={[10, 10, 16]} height={[53.375, 53.375, 152.8]} />

        <>
          {/* TITLES */}
          <Tweener
            t={t}
            tt={[
              [0.013, 0.03],
              [0.013, 0.03],
              [0.037, 0.075],
            ]}
          >
            <GivebackInitialTitle mb={[12, 12, 16]}>
              donated by the Lemonade community to 34 nonprofits in Giveback 2020
            </GivebackInitialTitle>
          </Tweener>
          <Giveback2020Floater
            t={t}
            tt={[
              [0.024, 0.024],
              [0.024, 0.024],
              [0.033, 0.033],
            ]}
            top={[417, 417, 560]}
            left={[138, 138, 868]}
            width={[98, 98, 168]}
            gray={undefined}
          >
            Giveback
            <br />
            2020
          </Giveback2020Floater>
          <Tweener
            t={t}
            tt={[
              [0.016, 0.027],
              [0.016, 0.027],
              [0.012, 0.025],
            ]}
            tween={riseTween}
          >
            <Title mb={[242, 242, 186]} height={[92, 92, 260]}>
              Here’s what that looks like:
            </Title>
          </Tweener>
        </>

        <>
          {/* COVID */}
          <Tweener
            t={t}
            tt={[
              [0.022, 0.055],
              [0.022, 0.055],
              [0.0385, 0.1105],
            ]}
          >
            <Giveback2020Covid mb={[30, 30, 26]} width={[221, 221, 442]} height={[221, 221, 442]} t={t} />
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.052, 0.072],
              [0.052, 0.072],
              [0.085, 0.115],
            ]}
            tween={riseTween}
          >
            <Title zIndex="3" mb={[10, 10, 30]} height={[92, 92, 260]}>
              50,000
              <br />
              ICU patients
            </Title>
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.057, 0.077],
              [0.057, 0.077],
              [0.105, 0.135],
            ]}
            tween={riseTween}
          >
            <Subtitle mb={[38, 38, 66]} height={[40, 40, 30]}>
              <MobileText>
                Treated by critical care specialists
                <br />
                via Direct Relief.
              </MobileText>
              <DesktopText>Treated by critical care specialists via Direct Relief.</DesktopText>
            </Subtitle>
          </Tweener>
          <Giveback2020Floater
            t={t}
            tt={[
              [0.058, 0.058],
              [0.058, 0.058],
              [0.125, 0.135],
            ]}
            top={[888, 888, 1764]}
            left={[25, 25, 119]}
            width={[82, 82, 156]}
            gray={undefined}
          >
            COVID-19
          </Giveback2020Floater>
          <Tweener
            t={t}
            tt={[
              [0.065, 0.1],
              [0.065, 0.1],
              [0.12, 0.15],
            ]}
            tween={riseTween}
          >
            {({ t, style }) => (
              <Flex style={style} width={[375, 375, 800]} justifyContent="center" flexWrap="wrap">
                <Giveback2020RisingParagraph
                  t={t}
                  amount={791.1}
                  number={7900}
                  title="Meals, Masks & PPE"
                  text="Sent to seniors, homebound individuals, and frontline workers via Transfernation"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={85249.65}
                  number={22}
                  title="Blood Drives"
                  text="Organized for the supply of Covid-19 antibodies via the American Red Cross"
                  mobileText={undefined}
                />

                <Giveback2020RisingParagraph
                  t={t}
                  amount={9840.95}
                  number={980}
                  title="Lunches"
                  text="Served to those facing<br/>hunger in the U.S. during the pandemic via Feeding America"
                  mobileText="Served to those facing hunger in the U.S. during the pandemic via<br/>Feeding America"
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={10007.98}
                  number={3000}
                  title="Jobs"
                  text="Sourced for homeless mothers seeking employment in NYC via Women In Need"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={103813.82}
                  number={103}
                  title="Families"
                  text="Rent covered for three months via New Story"
                  mobileText={undefined}
                />
              </Flex>
            )}
          </Tweener>
        </>

        <>
          {/* TREES */}
          <Tweener
            t={t}
            tt={[
              [0.145, 0.18],
              [0.145, 0.18],
              [0.18, 0.21],
            ]}
          >
            <Giveback2020Trees
              mt={[101, 101, 130]}
              mb={[15, 15, 40]}
              width={[206, 206, 442]}
              height={[180, 180, 360]}
              t={0}
            />
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.15, 0.17],
              [0.15, 0.17],
              [0.195, 0.225],
            ]}
            tween={riseTween}
          >
            <Title zIndex="3" mb={[10, 10, 30]} height={[46, 46, 130]}>
              36,963 trees
            </Title>
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.16, 0.18],
              [0.16, 0.18],
              [0.205, 0.235],
            ]}
            tween={riseTween}
          >
            <Subtitle mb={[38, 38, 72]} height={[40, 40, 30]}>
              <MobileText>
                Planted across critical ecosystems in
                <br />
                North America via American Forests.
              </MobileText>
              <DesktopText>Planted across critical ecosystems in North America via American Forests.</DesktopText>
            </Subtitle>
          </Tweener>
          <Giveback2020Floater
            t={t}
            tt={[
              [0.19, 0.19],
              [0.19, 0.19],
              [0.24, 0.25],
            ]}
            top={[1732, 1732, 3022]}
            left={[280, 280, 767]}
            width={[98, 98, 156]}
            gray={undefined}
          >
            Environment
          </Giveback2020Floater>
          <Tweener
            t={t}
            tt={[
              [0.17, 0.205],
              [0.17, 0.205],
              [0.215, 0.245],
            ]}
            tween={riseTween}
          >
            {({ t, style }) => (
              <Flex style={style} width={[375, 375, 800]} justifyContent="center" flexWrap="wrap">
                <Giveback2020RisingParagraph
                  t={t}
                  amount={52687.05}
                  number={250}
                  title="Buses"
                  text="Provided to environmental activists for mass mobilizations via 350.org"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={56531.69}
                  number={77000}
                  title="Conservationists"
                  text="Provided with a free library of imagery, videos, and<br />other resources via<br />the Ocean Agency"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={3483.06}
                  number={100}
                  title="Beach Cleanups"
                  text="Organized throughout the U.S., picking up thousands of pounds of litter via Alliance for the Great Lakes"
                  mobileText={undefined}
                />
              </Flex>
            )}
          </Tweener>
        </>

        <>
          {/* VOTERS */}
          <Tweener
            t={t}
            tt={[
              [0.24, 0.25],
              [0.24, 0.25],
              [0.28, 0.29],
            ]}
          >
            <Giveback2020Voters
              mt={[30, 30, 127]}
              mb={[95, 95, 44]}
              width={[292, 292, 586]}
              height={[152, 152, 304]}
              t={undefined}
            />
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.24, 0.26],
              [0.24, 0.26],
              [0.27, 0.3],
            ]}
            tween={riseTween}
          >
            <Title mb={[10, 10, 30]} height={[92, 92, 260]}>
              250 voter
              <br />
              registration kits
            </Title>
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.25, 0.27],
              [0.25, 0.27],
              [0.29, 0.32],
            ]}
            tween={riseTween}
          >
            <Subtitle mb={[38, 38, 66]} height={[40, 40, 30]}>
              <MobileText>
                Distributed across the country via
                <br />
                March For Our Lives.
              </MobileText>
              <DesktopText>Distributed across the country via March For Our Lives.</DesktopText>
            </Subtitle>
          </Tweener>
          <Giveback2020Floater
            t={t}
            tt={[
              [0.265, 0.266],
              [0.265, 0.266],
              [0.32, 0.33],
            ]}
            top={[2467, 2467, 4057]}
            left={['2px', '2px', '-3px']}
            width={[68, 68, 178]}
            gray={undefined}
          >
            Civil
            <br />
            Rights
          </Giveback2020Floater>
          <Tweener
            t={t}
            tt={[
              [0.26, 0.295],
              [0.26, 0.295],
              [0.3, 0.33],
            ]}
            tween={riseTween}
          >
            {({ t, style }) => (
              <Flex style={style} width={[375, 375, 1024]} justifyContent="center" flexWrap="wrap">
                <Giveback2020RisingParagraph
                  t={t}
                  amount={7422.88}
                  number={100}
                  title="Job Training Programs"
                  text="Granted to refugee chefs seeking a culinary career via Emma's Torch"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={2484.09}
                  number={100000}
                  title="Abuse Images<br/>Removed"
                  text="To protect child sex trafficking victims via Thorn"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={5584.39}
                  number={186}
                  title="Trafficked Single Mothers"
                  text="Supplied with essential toiletries via A21"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={115000.0}
                  number={1}
                  title="Criminal Law<br/>Reform Project"
                  text="Backed to end racial inequities in the U.S. criminal justice system via the ACLU"
                  mobileText="Backed to end racial inequities in the U.S. criminal justice system<br/>via the ACLU"
                />
              </Flex>
            )}
          </Tweener>
        </>

        <>
          {/* SPEAKER */}
          <GrayBackground height={[1700, 1700, 2760]} top={[3030, 3030, 4800]} />

          <Tweener
            t={t}
            tt={[
              [0.34, 0.38],
              [0.34, 0.38],
              [0.355, 0.405],
            ]}
          >
            <Giveback2020Speaker
              mt={[182, 182, 286]}
              mb={[20, 20, 38]}
              width={[219, 219, 442]}
              height={[219, 219, 442]}
              t={undefined}
            />
          </Tweener>

          <Tweener
            t={t}
            tt={[
              [0.35, 0.37],
              [0.35, 0.37],
              [0.38, 0.41],
            ]}
            tween={riseTween}
          >
            <Title zIndex="3" mb={[10, 10, 28]} height={[92, 92, 260]}>
              1<br />
              rare cancer trial
            </Title>
          </Tweener>

          <Tweener
            t={t}
            tt={[
              [0.36, 0.38],
              [0.36, 0.38],
              [0.4, 0.43],
            ]}
            tween={riseTween}
          >
            <Subtitle mb={[38, 38, 56]} height={[40, 40, 30]}>
              <MobileText>
                Funded at Memorial Sloan Kettering
                <br />
                Cancer Center via Cycle For Survival.
              </MobileText>
              <DesktopText>Funded at Memorial Sloan Kettering Cancer Center via Cycle For Survival.</DesktopText>
            </Subtitle>
          </Tweener>

          <Giveback2020Floater
            gray
            t={t}
            ang={[45, 45]}
            tt={[
              [0.392, 0.393],
              [0.392, 0.393],
              [0.432, 0.442],
            ]}
            top={[3337, 3337, 5470]}
            left={[286, 286, 849]}
            width={[92, 92, 156]}
          >
            Global
            <br />
            Poverty
            <br />& Disease
          </Giveback2020Floater>

          <Tweener
            t={t}
            tt={[
              [0.37, 0.405],
              [0.37, 0.405],
              [0.41, 0.44],
            ]}
            tween={riseTween}
          >
            {({ t, style }) => (
              <Flex style={style} width={[375, 375, 1024]} justifyContent="center" flexWrap="wrap">
                <Giveback2020RisingParagraph
                  t={t}
                  amount={20279.8}
                  number={3000}
                  title="Safe Homes"
                  text="Built for families in underserved communities in the U.S. via<br />Habitat for Humanity"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={9899.32}
                  number={273}
                  title="Meals"
                  text="Delivered daily to the homeless in NYC via Evolve Creative Alliance"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={76745.36}
                  number={3837}
                  title="Classrooms"
                  text="Provided with clean drinking water, bathrooms, and hygiene training across Ghana, Guatemala, and Laos via Pencils for Promise"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={10616.19}
                  number={25640}
                  title="Food Packets"
                  text="Sent to help severely malnourished children<br />via UNICEF"
                  mobileText={undefined}
                />
              </Flex>
            )}
          </Tweener>
        </>

        <>
          {/* STUDENTS */}
          <Tweener
            t={t}
            tt={[
              [0.437, 0.48],
              [0.437, 0.48],
              [0.465, 0.495],
            ]}
          >
            <Giveback2020Students
              mt={[57, 57, 110]}
              mb={[-29, -29, 60]}
              width={[268, 268, 268]}
              height={[221, 221, 257]}
              t={undefined}
            />
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.435, 0.455],
              [0.435, 0.455],
              [0.46, 0.49],
            ]}
            tween={riseTween}
          >
            <Title zIndex="3" mb={[10, 10, 30]} height={[92, 92, 260]}>
              355,500
              <br />
              students
            </Title>
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.445, 0.465],
              [0.445, 0.465],
              [0.48, 0.51],
            ]}
            tween={riseTween}
          >
            <Subtitle mb={[10, 10, 66]} height={[60, 60, 60]}>
              <MobileText>
                Supported by 6,192 teachers in 2,450 schools in
                <br />
                high-need communities across the U.S.
                <br />
                via Teach For America.
              </MobileText>
              <DesktopText>
                Supported by 6,192 teachers in 2,450 schools in high-need communities across the U.S.
                <br />
                via Teach For America.
              </DesktopText>
            </Subtitle>
          </Tweener>
          <Giveback2020Floater
            gray
            t={t}
            ang={[45, 45]}
            tt={[
              [0.495, 0.496],
              [0.495, 0.496],
              [0.515, 0.515],
            ]}
            top={[4073, 4073, 6546]}
            left={[22, 22, 161]}
            width={[92, 92, 156]}
          >
            Education
          </Giveback2020Floater>
          <Tweener
            t={t}
            tt={[
              [0.46, 0.5],
              [0.46, 0.5],
              [0.49, 0.52],
            ]}
            tween={riseTween}
          >
            {({ t, style }) => (
              <Flex style={style} width={[375, 375, 1024]} justifyContent="center" flexWrap="wrap">
                <Giveback2020RisingParagraph
                  t={t}
                  amount={13481.75}
                  number={100}
                  title="Female Coders"
                  text="Taught programming at safe schools in Afghanistan via Code To Inspire"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={3426.6}
                  number={5}
                  title="Laptops"
                  text="Sent to Israeli and Palestinian youth working on tech initiatives together via MEET"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={8340.26}
                  number={816}
                  title="Low Income Teens"
                  text="Supported through middle school in NYC via Robin Hood"
                  mobileText="Supported through middle school in NYC<br/>via Robin Hood"
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={41767.12}
                  number={380000}
                  title="Audio Lessons"
                  text="Taught to children in Pakistan via the Malala Fund"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={30845}
                  number={537}
                  title="Workshops Against Police Brutality"
                  text="Organized for young people from the SF Bay Area via Youth Speaks"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={5214.02}
                  number={29}
                  title="High-Need Communities"
                  text="Provided with teaching assistants in 250 American schools via City Year"
                  mobileText={undefined}
                />
              </Flex>
            )}
          </Tweener>
        </>

        <>
          {/* LGBTQ+ */}
          <Tweener
            t={t}
            tt={[
              [0.55, 0.6],
              [0.55, 0.6],
              [0.584, 0.61],
            ]}
          >
            <Giveback2020LGBT
              mt={[145, 145, 385]}
              mb={[20, 20, 107]}
              width={[287, 287, 565]}
              height={[144, 144, 297]}
              t={undefined}
            />
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.55, 0.57],
              [0.55, 0.57],
              [0.59, 0.62],
            ]}
            tween={riseTween}
          >
            <Title zIndex="3" mb={[10, 10, 31]} height={[92, 92, 260]}>
              1,500
              <br />
              young adults
            </Title>
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.1, 0.2],
              [0.1, 0.2],
              [0.61, 0.64],
            ]}
            tween={riseTween}
          >
            <Subtitle mb={[38, 38, 66]} height={[40, 40, 30]}>
              Provided with life-affirming crisis intervention services via The Trevor Project.
            </Subtitle>
          </Tweener>
          <Giveback2020Floater
            t={t}
            tt={[
              [0.615, 0.616],
              [0.615, 0.616],
              [0.63, 0.64],
            ]}
            top={[4988, 4988, 8147]}
            left={[294, 294, 836]}
            width={[78, 78, 156]}
            gray={undefined}
          >
            LGBTQ+
          </Giveback2020Floater>
          <Tweener
            t={t}
            tt={[
              [0.56, 0.58],
              [0.56, 0.58],
              [0.62, 0.63],
            ]}
            tween={riseTween}
          >
            {({ t, style }) => (
              <Flex style={style} width={[760, 760, 1000]} justifyContent="center" flexWrap="wrap">
                <Giveback2020RisingParagraph
                  t={t}
                  amount={3863.79}
                  number={1}
                  title="Transgender Safe Home"
                  text="Funded for trans clients via Ali Forney Center"
                  mobileText={undefined}
                />
              </Flex>
            )}
          </Tweener>
        </>

        <>
          {/* FAMILIES */}
          <Tweener
            t={t}
            tt={[
              [0.62, 0.67],
              [0.62, 0.67],
              [0.655, 0.695],
            ]}
          >
            <Giveback2020Families
              mt={[59, 59, 92]}
              mb={[86, 86, 40]}
              width={[164, 164, 328]}
              height={[219, 219, 437]}
              t={undefined}
            />
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.637, 0.657],
              [0.637, 0.657],
              [0.68, 0.71],
            ]}
            tween={riseTween}
          >
            <Title zIndex="3" mb={[10, 10, 31]} height={[92, 92, 260]}>
              14,591
              <br />
              veterans
            </Title>
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.65, 0.67],
              [0.65, 0.67],
              [0.7, 0.73],
            ]}
            tween={riseTween}
          >
            <Subtitle mb={[38, 38, 66]} height={[40, 40, 30]}>
              <MobileText>
                Connected to peer support and wellness
                <br />
                resources via Objective Zero Foundation.
              </MobileText>
              <DesktopText>Connected to peer support and wellness resources via Objective Zero Foundation.</DesktopText>
            </Subtitle>
          </Tweener>
          <Giveback2020Floater
            t={t}
            tt={[
              [0.695, 0.696],
              [0.695, 0.696],
              [0.715, 0.715],
            ]}
            top={[5628, 5628, 9344]}
            left={[19, 19, 188]}
            width={[86, 86, 156]}
            gray={undefined}
          >
            Mental
            <br />
            Health
            <br />
            Support
          </Giveback2020Floater>
          <Tweener
            t={t}
            tt={[
              [0.66, 0.695],
              [0.66, 0.695],
              [0.71, 0.74],
            ]}
            tween={riseTween}
          >
            {({ t, style }) => (
              <Flex style={style} width={[375, 375, 1024]} justifyContent="center" flexWrap="wrap">
                <Giveback2020RisingParagraph
                  t={t}
                  amount={9739.94}
                  number={50000}
                  title="Families of the Addiction Crisis"
                  text="Provided with online addiction resources<br/>via Shatterproof"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={53977.66}
                  number={4800}
                  title="Mental Health Patients"
                  text="Given treatment and recovery scholarships via TWLOHA"
                  mobileText="Given treatment and recovery scholarships<br/>via TWLOHA"
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={3635.62}
                  number={351}
                  title="Emergency Responses"
                  text="Administered to those in a mental health crisis across the U.S. via Crisis Text Line"
                  mobileText={undefined}
                />
                <Giveback2020RisingParagraph
                  t={t}
                  amount={34000.2}
                  number={80}
                  title="High School Trainings"
                  text="Trained in teen mental health first aid via the<br />Born This Way Foundation"
                  mobileText={undefined}
                />
              </Flex>
            )}
          </Tweener>
        </>

        <>
          {/* ZOO */}
          <Tweener
            t={t}
            tt={[
              [0.75, 0.81],
              [0.75, 0.81],
              [0.78, 0.9],
            ]}
          >
            <Giveback2020Zoo mt={[50, 50, 0]} width={[989, 989, 1676]} height={[770, 770, 2121]} t={undefined} />
          </Tweener>
        </>

        <>
          {/* FINITO */}
          <Tweener
            t={t}
            tt={[
              [0.78, 0.82],
              [0.78, 0.82],
              [0.86, 0.92],
            ]}
            tween={riseAlotTween}
          >
            <Giveback2020Finito width={[1200, 1500, 5000]} height={[1200, 1200, 4000]} bottom={[-1000, -1000, -3650]} />
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.81, 0.85],
              [0.81, 0.85],
              [0.91, 0.96],
            ]}
          >
            <Giveback2020FinalDot t={undefined} />
          </Tweener>
          <Tweener
            t={t}
            tt={[
              [0.1, 0.2],
              [0.1, 0.2],
              [0.91, 0.935],
            ]}
            tween={riseTween}
          >
            <Title zIndex="2" color="white" mt={[100, 100, 0]} mb={[26, 26, 56]}>
              Join the Giveback Movement
            </Title>
            <SmallerSubtitleLink
              zIndex="2"
              color="white"
              mb={[26, 26, 20]}
              onClick={share}
              data-event="giveback2020.share_the_numbers.clicked"
            >
              Share the numbers
            </SmallerSubtitleLink>
            <SmallerSubtitle zIndex="2" color="white" mb={[50, 50, 45]}>
              <MobileText>
                Hundreds of thousands of members already support causes they care about, simply by getting a Lemonade
                policy. As our Lemonade community grows, your social impact can become even stronger.
              </MobileText>
              <DesktopText>
                Hundreds of thousands of members already support causes they
                <br />
                care about, simply by getting a Lemonade policy. As our Lemonade
                <br />
                community grows, your social impact can become even stronger.
              </DesktopText>
            </SmallerSubtitle>
            <Box zIndex="2" width={[200, 200, 207]} mb={[110, 110, 207]}>
              <a href={nav.giveback} data-event="giveback2020.learn_more.clicked">
                <Button style={{ width: '100%' }} flavor="primary">
                  Learn More
                </Button>
              </a>
            </Box>
          </Tweener>
          <Gradient height={[500, 500, 700]} />
        </>
      </PageContainer>
      <PathContainer>
        <Giveback2020Path t={t} />
      </PathContainer>
    </Container>
  );
});

Giveback2020Internal.displayName = 'Giveback2020Internal';

export default function Giveback2020Page() {
  const { isTrackPad } = useTrackPad();
  const { relativeScroll: t } = usePageScroll();

  const [render, setRender] = React.useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setRender(true);
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (t === 0) return;

    const tempScroll = window.scrollY;

    setTimeout(() => {
      window.scrollTo(0, 0);
      setTimeout(() => {
        window.scrollTo(0, tempScroll);
      }, 15);
    }, 15);
  }, []);

  const actualT = useOptionalTween(t, !isTrackPad, 10000);

  if (!render) return null;

  return (
    <Layout
      {...sharedConfig['en-US'].layout}
      page={GIVEBACK_LANDING_PAGE}
      {...{
        CustomHeaderCTA: (
          <GivebackShareButton
            event="giveback2020.header_share.clicked"
            url="https://bit.ly/315hwZd"
            content="Best part of 2020: I just helped donate $1,128,109.85 to charities with my Lemonade policy via the Giveback! #GoLemonade #Giveback2020 via @lemonade_inc'"
          />
        ),
        hideAskUsAnything: true,
        pt: [90, 90, 170],
      }}
    >
      <SEO
        title="The 2020 Lemonade Giveback"
        metaTitle="The 2020 Lemonade Giveback"
        description="$1,128,109.85 donated to 34 charities by the Lemonade community in the 2020 Giveback. Here’s the impact that made."
        ogImage={ogImage.src}
      />
      <Giveback2020Internal t={actualT} />
    </Layout>
  );
}
